import { Box, Button, Icon, IconButton, Stack, Typography } from '@mui/material';
import { User } from '@schooly/api';
import { useNotifications } from '@schooly/components/notifications';
import { useFileDropZone } from '@schooly/hooks/use-file-drop-zone';
import {
  CheckIcon,
  CrossIcon,
  DropZoneBlock,
  ImageFormatIcon,
  ImageSizeIcon,
  ModalConfirm,
  ModalContent,
  ModalFooter,
  ModalMain,
  Spin,
} from '@schooly/style';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getUserFullName } from '../../../helpers/users';
import { AttachmentFile } from '../../ui/Input/RichText/Attachments/attachment-types';
import { ModalHeader } from '../../uikit-components/Modal/ModalHeader';

const IMAGE_ACCEPT = { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] };
const MAX_SIZE_KB = 200;
const MAX_SIZE = MAX_SIZE_KB * 1024;
const IMAGE_FORMAT_LABEL = '.png, .jpeg';

interface AvatarEditModalProps {
  onClose: () => void;
  handleChange: () => void;
  isOpen: boolean;
  isUploading: boolean;
  user?: User;
  file?: AttachmentFile;
  setFile: (file?: AttachmentFile) => void;
}

const AvatarEditModal = ({
  onClose,
  isOpen,
  user,
  file,
  setFile,
  handleChange,
  isUploading,
}: AvatarEditModalProps) => {
  const { formatMessage } = useIntl();
  const { showError } = useNotifications();
  const [hasError, setHasError] = useState(false);

  const handleDrop = useCallback(
    (droppedFile: File) => {
      setHasError(false);
      setFile({
        id: `${droppedFile.name}${Math.floor(Math.random() * 100)}`,
        file: droppedFile,
      });
    },
    [setFile],
  );

  const handleError = useCallback(
    (message: string) => {
      setHasError(true);
      showError({ message });
    },
    [showError],
  );

  const { getRootProps, getInputProps, isDragActive } = useFileDropZone({
    accept: IMAGE_ACCEPT,
    maxSize: MAX_SIZE,
    onDrop: handleDrop,
    onError: handleError,
    errorMessages: {
      fileTooLarge: formatMessage({ id: 'dnd-Error-FileIsLarger' }, { size: `${MAX_SIZE_KB} KB` }),
      fileInvalidType: formatMessage(
        { id: 'dnd-Error-InvalidFormat' },
        { format: IMAGE_FORMAT_LABEL },
      ),
    },
  });

  const preview = useMemo(
    () => (file?.file ? URL.createObjectURL(file.file) : undefined),
    [file?.file],
  );

  useEffect(() => {
    if (!isOpen) {
      setFile(undefined);
      setHasError(false);
    }
  }, [isOpen, setFile]);

  if (!user) return null;

  return (
    <ModalConfirm open={isOpen} fullWidth onClose={onClose}>
      <ModalHeader title={getUserFullName(user)} active>
        <IconButton onClick={onClose}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>
      <ModalMain>
        <ModalContent active>
          <Stack alignItems="center" justifyContent="center">
            <DropZoneBlock
              {...getRootProps()}
              active={isDragActive}
              error={hasError}
              sx={{
                width: 330,
                height: 330,
                borderRadius: '50%',
                overflow: 'hidden',
                p: 0,
              }}
            >
              <input {...getInputProps()} />
              {file ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    background: `center / cover no-repeat url(${preview})`,
                    opacity: isDragActive ? 0.7 : 1,
                  }}
                />
              ) : (
                <Stack alignItems="center" justifyContent="center" width="60%">
                  <Typography variant="h3" color="inherit">
                    {formatMessage({ id: 'dnd-Image' })}
                  </Typography>
                  <Typography color="inherit">
                    {formatMessage(
                      { id: 'dnd-BrowseAFile' },
                      {
                        browse: (
                          <Typography component="span" color="primary.main">
                            {formatMessage({ id: 'dnd-Browse' })}
                          </Typography>
                        ),
                      },
                    )}
                  </Typography>
                  <Stack direction="row" mt={2.5} gap={2}>
                    <Stack direction="row" gap={0.5} whiteSpace="nowrap" alignItems="center">
                      <Icon fontSize="small">
                        <ImageSizeIcon />
                      </Icon>
                      <Typography color="inherit" variant="caption">
                        {formatMessage({ id: 'dnd-MaxSize' }, { size: `${MAX_SIZE_KB} KB` })}
                      </Typography>
                    </Stack>
                    <Stack direction="row" gap={1} whiteSpace="nowrap" alignItems="center">
                      <Icon fontSize="small">
                        <ImageFormatIcon />
                      </Icon>
                      <Typography color="inherit" variant="caption">
                        {formatMessage({ id: 'dnd-Format' }, { format: IMAGE_FORMAT_LABEL })}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              )}
            </DropZoneBlock>
          </Stack>
        </ModalContent>
      </ModalMain>
      <ModalFooter active>
        <Button variant="outlined" disabled={isUploading} onClick={onClose}>
          <FormattedMessage id="action-Cancel" />
        </Button>
        <Button
          disabled={isUploading}
          endIcon={isUploading ? <Spin /> : <CheckIcon />}
          onClick={handleChange}
        >
          <FormattedMessage id="action-Save" />
        </Button>
      </ModalFooter>
    </ModalConfirm>
  );
};

export default AvatarEditModal;
